import type { CosmeticDef } from './cosmetics';

/**
 * How a tracer is drawn. Purely visual: every style keeps the weapon's own tracer width and lifetime, so a
 * tracer never changes how visible a shot is for opponents in a way that matters for gameplay.
 * - solid: one colored beam. - rainbow: hue cycling along the beam. - dotted: a row of dashes/cubes.
 * - pulse: beads that swell and fade between two colors. - sparkle: a faint beam with twinkling glints.
 */
export type TracerStyle = 'solid' | 'rainbow' | 'dotted' | 'pulse' | 'sparkle';

export interface TracerDef extends CosmeticDef {
  style: TracerStyle;
  /**
   * Palette: solid = [color]; dotted = [color]; pulse and sparkle = [main, secondary]; rainbow ignores it.
   * Empty means "use the weapon's own tracer color" (the Factory tracer).
   */
  colors: number[];
}

/**
 * Per-weapon bullet tracers (hitscan weapons only). Pricing mirrors camos: commons are coin-priced (earned by
 * playing), flashy styles cost gems. None of them affect power.
 */
export const TRACERS: TracerDef[] = [
  { id: 'none', name: 'Factory', rarity: 'common', price: 0, currency: 'coins', unlockLevel: 1, source: 'starter', style: 'solid', colors: [] },
  // Coin-priced commons (solid colors).
  { id: 'crimson', name: 'Crimson', rarity: 'common', price: 600, currency: 'coins', unlockLevel: 3, source: 'shop', style: 'solid', colors: [0xff4a5a] },
  { id: 'lime', name: 'Lime', rarity: 'common', price: 600, currency: 'coins', unlockLevel: 4, source: 'shop', style: 'solid', colors: [0x7aff4a] },
  { id: 'azure', name: 'Azure', rarity: 'common', price: 900, currency: 'coins', unlockLevel: 6, source: 'shop', style: 'solid', colors: [0x4ab0ff] },
  { id: 'sunny', name: 'Sunny', rarity: 'common', price: 900, currency: 'coins', unlockLevel: 8, source: 'shop', style: 'solid', colors: [0xffe84a] },
  { id: 'bubblegum_pink', name: 'Bubblegum Pink', rarity: 'common', price: 1200, currency: 'coins', unlockLevel: 10, source: 'shop', style: 'solid', colors: [0xff8ad0] },
  // Gem shop (animated styles).
  { id: 'bubble_trail', name: 'Bubble Trail', rarity: 'rare', price: 100, currency: 'gems', unlockLevel: 1, source: 'shop', style: 'dotted', colors: [0x7ad8ff] },
  { id: 'pixel_dots', name: 'Pixel Dots', rarity: 'rare', price: 120, currency: 'gems', unlockLevel: 1, source: 'shop', style: 'dotted', colors: [0x6aff8a] },
  { id: 'frostbite', name: 'Frostbite', rarity: 'rare', price: 150, currency: 'gems', unlockLevel: 1, source: 'shop', style: 'pulse', colors: [0xbfefff, 0x4a9aff] },
  { id: 'neon_pulse', name: 'Neon Pulse', rarity: 'epic', price: 200, currency: 'gems', unlockLevel: 1, source: 'shop', style: 'pulse', colors: [0xff4ad8, 0x3affff] },
  { id: 'ember', name: 'Ember', rarity: 'epic', price: 200, currency: 'gems', unlockLevel: 1, source: 'shop', style: 'sparkle', colors: [0xff7a1a, 0xffd24a] },
  { id: 'starlight', name: 'Starlight', rarity: 'epic', price: 220, currency: 'gems', unlockLevel: 1, source: 'shop', style: 'sparkle', colors: [0xfff0a0, 0xffffff] },
  { id: 'rainbow', name: 'Rainbow', rarity: 'legendary', price: 300, currency: 'gems', unlockLevel: 1, source: 'shop', style: 'rainbow', colors: [] },
];

export const TRACER_BY_ID: Record<string, TracerDef> = Object.fromEntries(TRACERS.map((t) => [t.id, t]));

// Shot shapes ----------------------------------------------------------------------------------------------------

/**
 * How a weapon's shots are drawn: a bright dash (`tail` m long, `px` screen pixels thick, wrapped in a glowing halo) that crosses
 * the shot in `life` seconds over a faint path line. It is a property of the WEAPON, never of a cosmetic tracer: cosmetics only
 * recolor and re-pattern the dash, so none can make a shot easier or harder to see than the weapon's own.
 */
export interface TracerShape {
  /** Core thickness in screen pixels, held at any distance. */
  px: number;
  /** Halo thickness as a multiple of the core. */
  halo: number;
  /** Length of the bright dash in meters (long shots use at least a fifth of their length so the dash stays continuous). */
  tail: number;
  /** Seconds the whole shot (dash crossing + fade) takes. */
  life: number;
  /** Brightness of the faint path line (0 = none). */
  trail: number;
}

/** Shape by weapon class; a weapon names its class in {@link TRACER_SHAPE_OF}. */
export const TRACER_SHAPES: Record<string, TracerShape> = {
  /** Sidearms: thin and short. */
  pistol: { px: 1.7, halo: 3, tail: 3, life: 0.12, trail: 0.16 },
  /** A big bore: a fat, slower bolt. */
  magnum: { px: 2.8, halo: 3.2, tail: 5, life: 0.16, trail: 0.22 },
  /** Fast-firing SMGs: thin, many of them, so they stay readable rather than a curtain. */
  smg: { px: 1.6, halo: 3, tail: 3.5, life: 0.1, trail: 0.14 },
  /** Rifles and carbines. */
  rifle: { px: 2.2, halo: 3, tail: 6, life: 0.14, trail: 0.2 },
  /** Machine guns: heavier rounds. */
  heavy: { px: 2.8, halo: 3.2, tail: 6, life: 0.14, trail: 0.2 },
  /** Marksman rifle: a long fine bolt. */
  marksman: { px: 2.6, halo: 3, tail: 9, life: 0.17, trail: 0.24 },
  /** Shotgun pellets: many fine sparks. */
  shotgun: { px: 1.3, halo: 2.4, tail: 2.5, life: 0.1, trail: 0.12 },
  /** Piercing sniper rounds: a big, long, lingering beam you can follow back to the shooter. */
  sniper: { px: 3.6, halo: 3.4, tail: 14, life: 0.24, trail: 0.3 },
  /** The railgun: the biggest. */
  rail: { px: 4.6, halo: 3.4, tail: 18, life: 0.28, trail: 0.34 },
};

export const TRACER_SHAPE_OF: Record<string, string> = {
  brick_rifle: 'rifle',
  buzz_smg: 'smg',
  scattergun: 'shotgun',
  pulse_carbine: 'rifle',
  tempest_ar: 'rifle',
  twin_barrel: 'shotgun',
  hornet_smg: 'smg',
  ranger_br: 'marksman',
  thumper_lmg: 'heavy',
  wasp_pdw: 'smg',
  auto_scatter: 'shotgun',
  voxel_shredder: 'smg',
  peashooter: 'pistol',
  hand_cannon: 'magnum',
  machine_pistol: 'pistol',
  sawed_off: 'shotgun',
  photon_pistol: 'pistol',
  railgun: 'rail',
  longshot: 'sniper',
  hunter_dmr: 'sniper',
  pierce_rifle: 'sniper',
};

/** The shot shape of a hitscan weapon (by id); undefined for weapons that have none. */
export function tracerShapeFor(weaponId: string): TracerShape | undefined {
  const cls = TRACER_SHAPE_OF[weaponId];
  return cls ? TRACER_SHAPES[cls] : undefined;
}
